import { Inject, Injectable } from '@angular/core';
import { times } from 'lodash-es';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { BrowserSupportedCapabilitiesService } from '@shared/services/browser-supported-capabilities.service';
import { APP_CONFIG, ConfigSettings } from '@shared/config';

declare var webkitSpeechRecognition: any;

enum VoiceEvents {
  Start = 'start',
  End = 'end',
  AudioStart = 'audiostart',
  AudioEnd = 'audioend',
  SpeechStart = 'speechstart',
  SpeechEnd = 'speechend',
  SoundStart = 'soundstart',
  SoundEnd = 'soundend',
  Result = 'result',
  Error = 'error',
  NoMatch = 'nomatch',
}

enum VoiceRecognitionError {
  NoSpeech = 'no-speech',
  Aborted = 'aborted',
  AudioCapture = 'audio-capture',
  Network = 'network',
  NotAllowed = 'not-allowed',
  ServiceNotAllowed = 'service-not-allowed',
  BadGrammar = 'bad-grammar',
  LanguageNotSupported = 'language-not-supported',
}

@Injectable({
  providedIn: 'root',
})
export class VoiceRecognitionService {
  private recognition;

  private transcript = new Subject<string[]>();
  private isListening = new BehaviorSubject<boolean>(false);
  private isCapturing = new BehaviorSubject<boolean>(false);
  private isSpeeching = new BehaviorSubject<boolean>(false);
  private isDetectingSound = new BehaviorSubject<boolean>(false);

  public readonly isVoiceRecognitionAllowed: boolean;
  public isListening$ = this.isListening.asObservable();
  public isSpeeching$ = this.isSpeeching.asObservable();
  public transcript$ = this.transcript.asObservable();

  private MAX_ALTERNATIVES = 5;
  private active: boolean = false;

  constructor(
    private browserSupportedCapabilities: BrowserSupportedCapabilitiesService,
    @Inject(APP_CONFIG) { speechRecognitionEnabled }: ConfigSettings
  ) {
    this.isVoiceRecognitionAllowed =
      this.browserSupportedCapabilities.isVoiceRecognitionAllowed() &&
      speechRecognitionEnabled;
    this.init();
  }

  private init(): void {
    if (this.isVoiceRecognitionAllowed) {
      this.recognition = new webkitSpeechRecognition();
      this.recognition.maxAlternatives = this.MAX_ALTERNATIVES;
      this.recognition.interimResults = false;
      this.recognition.lang = 'en-US';
      this.initEvents();
    }
  }

  private initEvents(): void {
    this.recognition.addEventListener(VoiceEvents.Result, ev => {
      this.speechRecongitionResult(ev);
    });

    this.recognition.addEventListener(VoiceEvents.Start, () => {
      if (!this.isListening.value) {
        this.isListening.next(true);
      }
    });
    this.recognition.addEventListener(VoiceEvents.End, () => {
      if (this.active) this.recognition.start();
      else if (this.isListening.value) {
        this.isListening.next(false);
      }
    });

    this.recognition.addEventListener(VoiceEvents.AudioStart, () =>
      this.isCapturing.next(true)
    );
    this.recognition.addEventListener(VoiceEvents.AudioEnd, () =>
      this.isCapturing.next(false)
    );

    this.recognition.addEventListener(VoiceEvents.SpeechStart, () =>
      this.isSpeeching.next(true)
    );

    this.recognition.addEventListener(VoiceEvents.SpeechEnd, () =>
      this.isSpeeching.next(false)
    );

    this.recognition.addEventListener(VoiceEvents.SoundStart, () =>
      this.isDetectingSound.next(false)
    );
    this.recognition.addEventListener(VoiceEvents.SoundEnd, () =>
      this.isDetectingSound.next(false)
    );

    this.recognition.onerror = (event: ErrorEvent) => {
      if (
        event.error === VoiceRecognitionError.NotAllowed ||
        event.error === VoiceRecognitionError.ServiceNotAllowed
      ) {
        console.warn('The browser has denied the use of the microphone.');
        this.stop();
      }
    };
  }

  start() {
    if (this.isVoiceRecognitionAllowed) {
      this.active = true;
      if (!this.isListening.value) {
        try {
          this.recognition.start();
        } catch (e) {
          console.error('There was an error when starting.', e);
          this.recognition.stop();
        }
      }
    }
  }

  stop() {
    if (this.isVoiceRecognitionAllowed) {
      this.active = false;
      if (this.isListening.value) {
        this.recognition.stop();
      }
    }
  }

  subscribeTranscript(): Observable<string[]> {
    return this.transcript$.pipe(filter(() => this.active));
  }

  private speechRecongitionResult(event) {
    const results: SpeechRecognitionResult = event.results.item(0);
    const resultAlternatives: SpeechRecognitionAlternative[] = times(
      results.length
    ).map(index => results.item(index));
    const transcriptAlternatives: string[] = resultAlternatives.map(
      transcript => transcript.transcript.replace(/\./g, '').toLowerCase()
    );

    this.transcript.next(transcriptAlternatives);
  }
}
